import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { ShieldCheck, KeyRound, Check, Fingerprint } from 'lucide-react';
import { useAccessToken } from '../auth/useAuth';
import { AddPasskeyForm, AddTotpForm } from '../mfa/FactorForms';
import {
  ONBOARDING_ENROLL_BODY, NO_PASSKEY_SUPPORT, PASSKEY_HINT,
  BACKUP_PROMPT_LABEL, BACKUP_PROMPT_BODY, BACKUP_DECLINE,
} from '../mfa/copy';
import { stepUpWebauthnOptions, stepUpWebauthnVerify, stepUpTotp } from '../api/mfa';
import type { EnrollmentStepUp } from '../api/mfa';
import { createApiClient, ApiError } from '../api/client';
import { persistActiveWorkspaceId } from '../workspace/WorkspaceProvider';
import { SUPPORT_EMAIL } from '../lib/support';
import { hasVisibleContent } from '@steward-app/steward-types';
import { INVISIBLE_NAME_MESSAGE } from '../lib/nameCopy';
import { slugify, clampSlug, slugTooShort, SLUG_MAX } from '../lib/workspaceSlug';
import { SlugShortNote } from '../workspace/SlugShortNote';
import type { BootstrapFounderRequest, CreateWorkspaceResponse } from '../api/types';
import { cls as formCls } from '../components/formStyles';
import { TERMS_ACCEPTANCE_REQUIRED } from '@steward-app/steward-types';
import { recordTermsAcceptance } from '../terms/recordTermsAcceptance';
import { TermsAcceptanceLine } from '../terms/TermsAcceptanceLine';

const ICON_STROKE = 1.75;

function Progress({ step }: { step: 1 | 2 }) {
  return (
    <div className="mb-4 text-[10px] font-bold uppercase tracking-[0.14em] text-slate font-sans">
      STEP {step} OF 2
    </div>
  );
}

function IconChip() {
  return (
    <div className="flex h-14 w-14 items-center justify-center rounded-token bg-bronze/12 text-bronze">
      <ShieldCheck size={26} strokeWidth={ICON_STROKE} aria-hidden />
    </div>
  );
}

/**
 * First-run onboarding (W1.11): the unified 2-step flow a brand-new,
 * member-less user (native or Google-first) lands on — replacing the old
 * separate "enrollment wall then founder-arrival-then-modal" sequence for
 * exactly this case. Mounted by {@link FirstRunGate}, ABOVE
 * `WorkspaceProvider` (same reasoning as `EnrollmentGate`: identity-scoped
 * work — enrolling a factor, then creating the caller's first workspace —
 * must work before any workspace exists to scope requests to).
 *
 * Step 1 folds welcome + factor enrollment + a live confirmation into one
 * page; step 2 creates the workspace directly (no modal, no separate arrival
 * screen — the existing `WelcomeOverlay`, shown once `WorkspaceProvider`
 * mounts, is the arrival moment). `onComplete` lets the gate re-check its own
 * factor/membership queries so it stops rendering this flow and falls through
 * to the app shell.
 */
export function OnboardingFlow({ onComplete, onAcceptInviteInstead }: {
  /**
   * Re-check the gate's own factor/membership queries. May return a promise —
   * `Step2.submit` awaits it before navigating home (steward-app#1642).
   *
   * ⚠️ Awaiting it here is NOT what closes the #1642 race (independent review
   * corrected an earlier version of this comment that claimed it was). The URL
   * is already `/` throughout onboarding — `FirstRunGate` wraps the whole `/`
   * tree (`router.tsx`) and renders this flow in place of its children, so
   * `navigate('/', { replace: true })` below is a same-path no-op; it does not
   * gate when `WorkspaceProvider` mounts. The actual fix is `FirstRunGate`'s own
   * `bootstrapping` state (see `finishOnboarding`'s doc there), which holds
   * `FirstRunGate`'s render on this flow — not `WorkspaceProvider` — until its
   * `/me` and `/me/factors` refetches have BOTH genuinely landed, regardless of
   * which of those two independent queries a given render notices first.
   *
   * Awaiting `onComplete()` here still earns its keep for a narrower reason:
   * without it, this component would drop back to its pre-submit state (e.g.
   * re-enable the "Create my space" button) the instant the fire-and-forget
   * call returns, before the workspace bootstrap it triggered has actually
   * finished — a confusing flash, not a redirect bug.
   */
  onComplete: () => void | Promise<void>;
  /**
   * Present only when the user also holds a pending invitation, so the affordance
   * never appears without somewhere to go. Offered on step 2, not step 1: enrolling
   * a sign-in key is required for either path, so there is nothing to opt out of
   * until the flow actually asks them to create an org. The gate handles this by
   * stepping aside rather than navigating (see FirstRunGate).
   */
  onAcceptInviteInstead?: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  // Which factor type step 1a actually enrolled — decides step 1c's initial
  // ceremony (auto passkey vs a typed TOTP code) and whether step 1c's
  // passkey-failure screen offers a TOTP fallback at all.
  const [primaryFactor, setPrimaryFactor] = useState<'webauthn' | 'totp' | null>(null);
  const [backupAdded, setBackupAdded] = useState(false);
  // Did an enrollment in this flow already mint the session's step-up (appcore
  // docs/29 F1)? Sticky-OR across enrollments: a view-tier step-up lasts ~12h, so
  // once ANY enrollment minted one it stays good for the rest of onboarding, and a
  // later enrollment that (for any reason) reports nothing must not un-mint it.
  const [stepUpMinted, setStepUpMinted] = useState(false);
  const noteStepUp = (s: EnrollmentStepUp) => { if (s.stepUpMinted === true) setStepUpMinted(true); };

  return (
    <div className="mx-auto flex max-w-[440px] flex-col items-center pt-[6vh] text-center">
      <Progress step={step} />
      <IconChip />
      {step === 1 ? (
        <Step1
          primaryFactor={primaryFactor}
          backupAdded={backupAdded}
          stepUpMinted={stepUpMinted}
          onPrimaryEnrolled={(type, stepUp) => { setPrimaryFactor(type); noteStepUp(stepUp); }}
          onBackupAdded={stepUp => { setBackupAdded(true); noteStepUp(stepUp); }}
          onConfirmed={() => setStep(2)}
        />
      ) : (
        <Step2 onComplete={onComplete} onAcceptInviteInstead={onAcceptInviteInstead} />
      )}
    </div>
  );
}

// ── Step 1: set up your sign-in key ──

type Step1Phase = 'enroll' | 'backup' | 'confirm';

function Step1({ primaryFactor, backupAdded, stepUpMinted, onPrimaryEnrolled, onBackupAdded, onConfirmed }: {
  primaryFactor: 'webauthn' | 'totp' | null;
  backupAdded: boolean;
  stepUpMinted: boolean;
  onPrimaryEnrolled: (type: 'webauthn' | 'totp', stepUp: EnrollmentStepUp) => void;
  onBackupAdded: (stepUp: EnrollmentStepUp) => void;
  onConfirmed: () => void;
}) {
  const token = useAccessToken();
  const supportsPasskey = browserSupportsWebAuthn();
  const [phase, setPhase] = useState<Step1Phase>('enroll');

  if (!token) return null;

  return (
    <>
      <h1 className="mb-2 mt-5 font-serif text-[26px] font-normal leading-tight tracking-[-0.01em] text-navy">
        Welcome. Let’s set up your space.
      </h1>
      {phase === 'enroll' && (
        <p className="m-0 mb-8 max-w-[380px] font-sans text-[14px] leading-relaxed text-mid">
          {ONBOARDING_ENROLL_BODY}
        </p>
      )}

      <div className="w-full text-left">
        {phase === 'enroll' && (
          supportsPasskey
            ? (
              <AddPasskeyForm
                token={token}
                buttonLabel="Add your passkey"
                hint={PASSKEY_HINT}
                onDone={stepUp => { onPrimaryEnrolled('webauthn', stepUp); setPhase('backup'); }}
              />
            )
            : (
              <div>
                <p className="mb-4 text-center text-[12px] text-slate font-sans">
                  {NO_PASSKEY_SUPPORT}
                </p>
                <AddTotpForm token={token} onDone={stepUp => { onPrimaryEnrolled('totp', stepUp); setPhase('confirm'); }} />
              </div>
            )
        )}

        {phase === 'backup' && (
          <BackupStep token={token} onAdded={onBackupAdded} onContinue={() => setPhase('confirm')} />
        )}

        {phase === 'confirm' && primaryFactor && (
          <ConfirmStep
            token={token}
            primaryFactor={primaryFactor}
            backupAdded={backupAdded}
            stepUpMinted={stepUpMinted}
            onConfirmed={onConfirmed}
          />
        )}
      </div>

      <RecoveryFooter />
    </>
  );
}

function BackupStep({ token, onAdded, onContinue }: { token: string; onAdded: (stepUp: EnrollmentStepUp) => void; onContinue: () => void }) {
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-token bg-bronze/12 text-bronze">
          <Check size={18} strokeWidth={ICON_STROKE} aria-hidden />
        </div>
        <div className="mb-4 text-[13px] text-navy font-sans">Authenticator app added. You’re all set.</div>
        <button onClick={onContinue} className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans">
          Continue
        </button>
      </div>
    );
  }

  if (adding) {
    return <AddTotpForm token={token} onDone={stepUp => { onAdded(stepUp); setDone(true); }} />;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3 rounded-token border border-line bg-cream/40 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-token bg-bronze/12 text-bronze">
          <Check size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </span>
        <span className="text-[13px] text-navy font-sans">Your passkey is set up.</span>
      </div>

      <div className="mb-2 text-[13px] font-semibold text-navy font-sans">{BACKUP_PROMPT_LABEL}</div>
      <p className="mb-4 text-[12px] leading-relaxed text-slate font-sans">
        {BACKUP_PROMPT_BODY}
      </p>
      <button
        onClick={() => setAdding(true)}
        className="flex w-full items-center justify-center gap-2 px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
      >
        <KeyRound size={15} strokeWidth={ICON_STROKE} aria-hidden />
        Add an authenticator app
      </button>
      <button
        onClick={onContinue}
        className="mt-2.5 w-full px-4 py-2 text-[11px] font-semibold cursor-pointer bg-transparent border-none text-slate font-sans hover:text-navy"
      >
        {BACKUP_DECLINE}
      </button>
    </div>
  );
}

type ConfirmPhase = 'already-confirmed' | 'passkey' | 'passkey-failed' | 'totp' | 'totp-submitting';

/**
 * 1c: makes sure a live view-tier step-up exists for `/onboarding/bootstrap`,
 * framed as confirming the just-added key works (its first genuine use). Auto-
 * initiates the passkey ceremony on mount when that's the primary factor — a
 * manual retry button covers browsers that decline to prompt without a fresh
 * click.
 *
 * `stepUpMinted` (appcore docs/29 F1) short-circuits the ceremony: appcore now
 * mints the session's step-up as part of a successful enrollment, so asking for
 * another passkey touch seconds later was pure repetition — it was the third
 * prompt in the original three-prompt onboarding.
 *
 * The CONFIRMATION BEAT is deliberately kept when the ceremony is skipped. This
 * step exists for a real reason beyond minting: it tells the user their new key
 * has been checked, instead of leaving them to wonder. Removing the friction must
 * not also remove the reassurance, so the skipped path still renders an explicit
 * "your key is set up and working" state with its own Continue.
 *
 * If the skip is ever wrong (an older appcore, a revoked row, an expired session),
 * nothing breaks: `/onboarding/bootstrap` 403s with STEP_UP_REQUIRED and the
 * central interceptor in api/client.ts runs the ceremony and retries. The skip is
 * an optimisation over a gate that still holds server-side, never a replacement
 * for it.
 */
function ConfirmStep({ token, primaryFactor, backupAdded, stepUpMinted, onConfirmed }: {
  token: string;
  primaryFactor: 'webauthn' | 'totp';
  backupAdded: boolean;
  stepUpMinted: boolean;
  onConfirmed: () => void;
}) {
  const [phase, setPhase] = useState<ConfirmPhase>(
    stepUpMinted ? 'already-confirmed' : primaryFactor === 'webauthn' ? 'passkey' : 'totp',
  );
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const attempted = useRef(false);

  async function tryPasskey() {
    setPhase('passkey');
    setError('');
    try {
      const optionsJSON = await stepUpWebauthnOptions(token);
      const response = await startAuthentication({ optionsJSON });
      await stepUpWebauthnVerify(token, { response });
      onConfirmed();
    } catch {
      setPhase('passkey-failed');
      setError('That was cancelled or timed out. Try again, or use a code instead.');
    }
  }

  useEffect(() => {
    if (stepUpMinted || primaryFactor !== 'webauthn' || attempted.current) return;
    attempted.current = true;
    void tryPasskey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryFactor, stepUpMinted]);

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setPhase('totp-submitting');
    setError('');
    try {
      await stepUpTotp(token, { code: code.trim() });
      onConfirmed();
    } catch {
      setPhase('totp');
      setCode('');
      setError('That code didn’t match. Check your authenticator app and try again.');
    }
  }

  const keyWord = primaryFactor === 'webauthn' ? 'passkey' : 'authenticator app';

  return (
    <div>
      <div className="mb-4 text-[13px] font-semibold text-navy font-sans">
        {phase === 'already-confirmed' ? 'Your key is set up and working.' : 'One quick check that your new key works.'}
      </div>

      {phase === 'already-confirmed' && (
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-token bg-bronze/12 text-bronze">
            <Check size={18} strokeWidth={ICON_STROKE} aria-hidden />
          </div>
          <p className="m-0 mb-4 text-[12px] leading-relaxed text-slate font-sans">
            We checked your {keyWord} as you added it, so there is nothing more to do here.
          </p>
          <button
            type="button"
            onClick={onConfirmed}
            className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
          >
            Continue
          </button>
        </div>
      )}

      {(phase === 'passkey') && (
        <div className="rounded-token border border-line bg-cream/40 px-4 py-3 text-center text-[13px] text-navy font-sans">
          Waiting for your passkey…
        </div>
      )}

      {phase === 'passkey-failed' && (
        <div>
          <button
            type="button"
            onClick={() => void tryPasskey()}
            className="flex w-full items-center justify-center gap-2 px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
          >
            <Fingerprint size={15} strokeWidth={ICON_STROKE} aria-hidden />
            Try again
          </button>
          {backupAdded && (
            <button
              type="button"
              onClick={() => { setPhase('totp'); setError(''); }}
              className="mt-2.5 flex w-full items-center justify-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-token border border-line-strong cursor-pointer bg-transparent text-navy uppercase tracking-[0.06em] font-sans"
            >
              <KeyRound size={14} strokeWidth={ICON_STROKE} aria-hidden />
              Use an authenticator code instead
            </button>
          )}
        </div>
      )}

      {(phase === 'totp' || phase === 'totp-submitting') && (
        <form onSubmit={e => void submitTotp(e)}>
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate font-sans" htmlFor="onboarding-confirm-totp">
            Code from your authenticator app
          </label>
          <input
            id="onboarding-confirm-totp"
            className="w-full px-3 py-2 text-[16px] tracking-[0.3em] text-center border border-line-strong rounded-token box-border bg-surface text-navy font-sans outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={6}
            autoFocus
            disabled={phase === 'totp-submitting'}
          />
          <button
            type="submit"
            disabled={phase === 'totp-submitting' || code.trim().length < 6}
            className="mt-3 w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {phase === 'totp-submitting' ? 'Verifying…' : 'Verify'}
          </button>
        </form>
      )}

      {error && <div className="mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{error}</div>}
    </div>
  );
}

function RecoveryFooter() {
  return (
    <p className="m-0 mt-8 max-w-[380px] text-[11px] leading-relaxed text-slate font-sans">
      Lost access to your passkey and your authenticator app? Contact us at{' '}
      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-navy underline">{SUPPORT_EMAIL}</a>{' '}
      and we’ll help you back in.
    </p>
  );
}

// ── Step 2: create your space ──

function Step2({ onComplete, onAcceptInviteInstead }: { onComplete: () => void | Promise<void>; onAcceptInviteInstead?: () => void }) {
  const token = useAccessToken();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [orgSlug, setOrgSlug] = useState('');
  const [orgSlugTouched, setOrgSlugTouched] = useState(false);
  const [wsName, setWsName] = useState('');
  const [wsSlug, setWsSlug] = useState('');
  const [wsSlugTouched, setWsSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const effectiveOrgSlug = orgSlugTouched ? clampSlug(orgSlug) : slugify(name);
  const effectiveWsName = wsName.trim() ? wsName : name;
  const effectiveWsSlug = wsSlugTouched ? clampSlug(wsSlug) : slugify(effectiveWsName);
  // A name of only invisible characters is held back like an empty one, with the reason shown
  // in the slot this form uses for its other errors (crossmarc/steward-app#2041).
  const invisibleName = [name, effectiveWsName].some(n => n.trim().length > 0 && !hasVisibleContent(n.trim()));

  // See FounderBootstrapDialog: held back before the request (crossmarc/steward-app#2050).
  const orgShort = slugTooShort(effectiveOrgSlug, name.trim().length > 0 || orgSlugTouched);
  const wsShort = slugTooShort(effectiveWsSlug, effectiveWsName.trim().length > 0 || wsSlugTouched);
  const slugShort = orgShort || wsShort;
  // Each short field gets its own message, shown once its name was left, the field was edited, or a
  // submit was attempted (no flash at the first keystroke). The button stays enabled so a click can explain.
  const [nameBlurred, setNameBlurred] = useState(false);
  const [wsNameBlurred, setWsNameBlurred] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const orgShortShown = orgShort && !invisibleName && (nameBlurred || orgSlugTouched || attempted);
  const wsShortShown = wsShort && !invisibleName
    && (wsNameBlurred || (!wsName.trim() && nameBlurred) || wsSlugTouched || attempted);

  // A click or Enter on an under-length address sends nothing, shows each short field's note and
  // moves focus to the first one. The effect runs after that render, so OnboardingFlow's collapsed
  // details are open by then.
  const [focusReq, setFocusReq] = useState<{ id: string } | null>(null);
  useEffect(() => {
    if (focusReq) document.getElementById(focusReq.id)?.focus();
  }, [focusReq]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || submitting || invisibleName) return;
    if (slugShort) { setAttempted(true); setShowDetails(true); setFocusReq({ id: orgShort ? 'onboarding-org-slug' : 'onboarding-ws-slug' }); return; }
    setSubmitting(true);
    setErr('');
    try {
      const payload: BootstrapFounderRequest = {
        tenantName: name.trim(),
        tenantSlug: effectiveOrgSlug,
        workspaceName: effectiveWsName.trim(),
        workspaceSlug: effectiveWsSlug,
      };
      // The click on "Create my space", with TermsAcceptanceLine beneath it, is the
      // acceptance (steward-app#2630). Recorded first: the façade refuses the bootstrap without it.
      await recordTermsAcceptance(token);
      const created = await createApiClient(token).post<CreateWorkspaceResponse>('/onboarding/bootstrap', payload);
      persistActiveWorkspaceId(created.workspaceId);
      // Await the refresh (steward-app#1642) before dropping this flow's own
      // "submitting" UI back to normal — see the doc on the `onComplete` prop
      // above for why this await does NOT itself gate the /select-workspace
      // race (FirstRunGate's `bootstrapping` flag does that).
      await onComplete();
      navigate('/', { replace: true });
    } catch (e2) {
      const ae = e2 instanceof ApiError ? e2 : null;
      setErr(
        ae && ae.status === 403 && ae.code !== 'STEP_UP_REQUIRED' && ae.code !== TERMS_ACCEPTANCE_REQUIRED
          ? `We couldn’t find an active subscription for your account. Contact us at ${SUPPORT_EMAIL} if you believe this is an error.`
          : ae && ae.status === 409
            ? 'That name is already taken. Try another.'
            : 'We couldn’t create your space just now. Try again in a moment.',
      );
      setSubmitting(false);
    }
  }

  const canSubmit = !!token && !submitting && !invisibleName && !!name.trim() && !!effectiveWsName.trim();

  return (
    <>
      <h1 className="mb-2 mt-5 font-serif text-[26px] font-normal leading-tight tracking-[-0.01em] text-navy">
        Create your space
      </h1>
      <p className="m-0 mb-8 max-w-[380px] font-sans text-[14px] leading-relaxed text-mid">
        This is where everything lives: documents, accounts, and the people
        you invite.
      </p>

      <form onSubmit={e => void submit(e)} className="w-full text-left">
        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans" htmlFor="onboarding-name">
          Name
        </label>
        <input
          id="onboarding-name"
          className="w-full px-3 py-2 text-[14px] border border-line-strong rounded-token box-border bg-surface text-navy font-sans outline-none mb-1 focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => setNameBlurred(true)}
          maxLength={200}
          placeholder="e.g. The Anderson Family"
          autoFocus
          required
        />
        <div className="mb-4 text-[11px] text-mid font-sans">You can change this anytime.</div>

        <button
          type="button"
          onClick={() => setShowDetails(v => !v)}
          className="mb-4 cursor-pointer border-none bg-transparent p-0 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-slate transition-colors duration-150 hover:text-navy"
        >
          Adjust the details
        </button>

        {/* Forced open while the short-address message shows, so the field it names is on screen
            (crossmarc/steward-app#2050). */}
        {(showDetails || orgShortShown || wsShortShown) && (
          <div className="mb-4 rounded-token border border-line bg-cream/40 p-4">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans" htmlFor="onboarding-org-slug">Web address</label>
            <input
              id="onboarding-org-slug"
              className={`${formCls.input} mb-1`}
              value={effectiveOrgSlug}
              onChange={e => { setOrgSlugTouched(true); setOrgSlug(e.target.value); }}
              pattern={'[a-z0-9\\-]+'}
              maxLength={SLUG_MAX}
              aria-invalid={orgShortShown || undefined}
              aria-describedby={orgShortShown ? 'onboarding-org-slug-short' : undefined}
            />
            {orgShortShown && <SlugShortNote id="onboarding-org-slug-short" />}
            <div className="mb-3 text-[11px] text-mid font-sans">Created from the name. Lowercase letters, numbers, and hyphens.</div>

            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans" htmlFor="onboarding-ws-name">Workspace name</label>
            <input
              id="onboarding-ws-name"
              className={`${formCls.input} mb-3`}
              value={effectiveWsName}
              onChange={e => setWsName(e.target.value)}
              onBlur={() => setWsNameBlurred(true)}
              maxLength={200}
            />

            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate font-sans" htmlFor="onboarding-ws-slug">Workspace web address</label>
            <input
              id="onboarding-ws-slug"
              className={`${formCls.input} mb-1`}
              value={effectiveWsSlug}
              onChange={e => { setWsSlugTouched(true); setWsSlug(e.target.value); }}
              pattern={'[a-z0-9\\-]+'}
              maxLength={SLUG_MAX}
              aria-invalid={wsShortShown || undefined}
              aria-describedby={wsShortShown ? 'onboarding-ws-slug-short' : undefined}
            />
            {wsShortShown && <SlugShortNote id="onboarding-ws-slug-short" />}
            <div className="text-[11px] text-mid font-sans">Created from the name. Lowercase letters, numbers, and hyphens.</div>
          </div>
        )}

        {(invisibleName || err) && <div className="mb-4 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{invisibleName ? INVISIBLE_NAME_MESSAGE : err}</div>}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Preparing your space…' : 'Create my space'}
        </button>
        <TermsAcceptanceLine className="m-0 mt-3 text-center" />
      </form>

      {onAcceptInviteInstead && (
        <div className="mt-7 w-full border-t border-line pt-5 text-left">
          {/* No promise about how long their own grant lasts: a trial grant expires
              (14 days), an invite grant does not, and this component cannot tell which
              it is. Saying "your space will keep" would be false for a trial. */}
          <p className="m-0 mb-2 font-sans text-[12px] leading-relaxed text-slate">
            You also have an invitation waiting to join a workspace already using Steward.
          </p>
          <button
            type="button"
            // ⚠️ Real `disabled`, not `aria-disabled` (independent review, steward-app#1642
            // follow-up): a disabled button's click event never fires at all — in the
            // DOM and under `fireEvent.click` alike — where `aria-disabled` is purely
            // advisory to assistive tech and does nothing to stop a click handler from
            // running. `submitting` is true from the moment `submit` starts (before the
            // POST even goes out) until `onComplete` — FirstRunGate's `finishOnboarding`,
            // which holds `bootstrapping` true for the same span — settles, so this
            // covers exactly the window where `steppedAside` firing would be able to
            // mount `WorkspaceProvider` on a stale `/me` (the FirstRunGate.tsx:99-101
            // race this button reopened a second door to). The `if (submitting) return;`
            // guard in the handler is belt and braces for the same reason `submit`
            // itself re-checks `submitting` rather than trusting the disabled button
            // alone: a synthetic click dispatched straight at the handler (bypassing the
            // DOM's own disabled short-circuit) would otherwise still get through.
            disabled={submitting}
            onClick={() => { if (submitting) return; onAcceptInviteInstead(); }}
            className="cursor-pointer border-none bg-transparent p-0 font-sans text-[12px] font-semibold text-navy underline decoration-navy/30 underline-offset-2 transition-colors duration-150 hover:decoration-navy disabled:cursor-not-allowed disabled:opacity-50"
          >
            Go to your invitation instead
          </button>
        </div>
      )}
    </>
  );
}
